import { useCallback } from "react";
import classNames from "classnames";
import { useFormikContext } from "formik";
import { useFlatDocumentCategories } from "@api/documentCategory.api";
import { TaskCreate } from "@common/contracts/task.contract";
import { isStatementRetrievalFinancialAccountType } from "@common/helpers/financialAccount";
import { FinancialAccount } from "@common/types/financialAccount";
import { TaskType } from "@common/types/task";
import { DocumentCategoryCombobox } from "@components/documents/DocumentCategorySelect/DocumentCategoryCombobox";
import { FinancialAccountCombobox } from "@components/financialAccount/AccountsFilter/FinancialAccountCombobox";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import fieldStyles from "./TaskDrawerFields.module.scss";
import { ACCOUNT_TYPE_TO_TAXONOMY } from "./statementRetrieval";
import { useWorkflowViewUpdater } from "./useWorkflowViewUpdater";

export const TaskStatementFields = () => {
    const formik = useFormikContext<TaskCreate>();
    const updateView = useWorkflowViewUpdater();
    const allAccounts = useFinancialAccounts();
    const categories = useFlatDocumentCategories();
    const { type, entityIds, view } = formik.values;
    const entityId = entityIds?.length === 1 ? entityIds[0] : undefined;

    const isStatementAccountOfEntity = useCallback(
        (account: FinancialAccount) =>
            account.entityId === entityId &&
            isStatementRetrievalFinancialAccountType(account.financialType),
        [entityId],
    );

    if (type !== TaskType.DOCUMENT_RETRIEVAL) {
        return null;
    }

    const selectedAccountId = view?.filters?.accountIds?.[0] ?? null;
    const selectedCategory =
        categories.find(
            (category) =>
                category.id === view?.filters?.documentCategoryIds?.[0],
        ) ?? null;

    const handleAccountChange = (accountId: number | null) => {
        const account = allAccounts.find(({ id }) => id === accountId);
        if (!account) {
            updateView({ accountIds: [] });
            return;
        }
        const taxonomy = ACCOUNT_TYPE_TO_TAXONOMY[account.financialType];
        const matchingCategory = taxonomy
            ? categories.find(
                  (category) => category.documentTaxonomy === taxonomy,
              )
            : undefined;
        updateView({
            accountIds: [account.id],
            ...(matchingCategory
                ? { documentCategoryIds: [matchingCategory.id] }
                : {}),
        });
    };

    return (
        <>
            <DescriptionListItem label="Financial account">
                <div className={fieldStyles.detailSelectWrapper}>
                    <FinancialAccountCombobox
                        value={selectedAccountId}
                        onValueChange={handleAccountChange}
                        disabled={entityId === undefined}
                        accountFilterPredicate={isStatementAccountOfEntity}
                        hideArrow={false}
                        buttonProps={{
                            variant: "primary",
                            block: false,
                            tooltip:
                                entityId === undefined
                                    ? "Select one entity first"
                                    : undefined,
                            className: classNames(
                                fieldStyles.detailSelectTrigger,
                                fieldStyles.justifyLeft,
                                {
                                    [fieldStyles.emptyValue]:
                                        selectedAccountId === null,
                                },
                            ),
                        }}
                    />
                </div>
            </DescriptionListItem>
            <DescriptionListItem label="Document category">
                <div className={fieldStyles.detailSelectWrapper}>
                    <DocumentCategoryCombobox
                        selectedCategory={selectedCategory}
                        onChange={(category) =>
                            updateView({
                                documentCategoryIds: category
                                    ? [category.id]
                                    : [],
                            })
                        }
                        placeholder="Select category"
                        triggerButtonProps={{
                            variant: "primary",
                            block: false,
                            className: classNames(
                                fieldStyles.detailSelectTrigger,
                                fieldStyles.justifyLeft,
                                {
                                    [fieldStyles.emptyValue]: !selectedCategory,
                                },
                            ),
                        }}
                        hideFooter
                    />
                </div>
            </DescriptionListItem>
        </>
    );
};
